import { CashActionType, isDefined, LoadableState, NbSelectItemOption } from '@eih-netbroker/common';
import { ReactComponent as ArrowRightIcon } from '../../../../assets/icons/arrow-right-line.svg';
import { NbNumericFormat } from '../../../../shared/components/numeric-format/numericFormat';
import { NbTextField } from '../../../../shared/components/text-field/TextField';
import { CashActionSourceAccountsUnion, CashActionTargetAccountsUnion } from '../../services/usePayment';
import { CashActionAccountSelector } from './CashActionAccountSelector';
import { CashActionNoSourceOrTargetAccountErrorFeedback } from './CashActionNoSourceOrTargetAccountErrorFeedback';
import classes from './CashActionParamEditor.module.scss';

type CashActionParamEditorProps<SourceAccountType, TargetAccountType> = {
  amount: string;
  cashActionType: CashActionType;
  setAmountToMaxLabel: string;
  sourceAccountAmount?: number;
  targetAccountAmount?: number;
  numberInputError?: string;
  selectedCurrency: string;
  selectedSourceAccount?: SourceAccountType;
  selectedTargetAccount?: TargetAccountType;
  sourceAccountOptions?: LoadableState<Array<NbSelectItemOption<SourceAccountType>>>;
  targetAccountOptions?: LoadableState<Array<NbSelectItemOption<TargetAccountType>>>;
  noTargetAccountsAvailable: boolean;
  handleAmountChange: (value: string) => void;
  handleSourceAccountChange?: (value: SourceAccountType | string) => void;
  handleTargetAccountChange: (value: TargetAccountType | string) => void;
};

export const CashActionParamEditor = <
  SourceAccountType extends CashActionSourceAccountsUnion,
  TargetAccountType extends CashActionTargetAccountsUnion,
>({
  amount,
  sourceAccountAmount,
  targetAccountAmount,
  selectedSourceAccount,
  selectedTargetAccount,
  selectedCurrency,
  sourceAccountOptions,
  targetAccountOptions,
  cashActionType,
  numberInputError,
  setAmountToMaxLabel,
  noTargetAccountsAvailable,
  handleAmountChange,
  handleSourceAccountChange,
  handleTargetAccountChange,
}: CashActionParamEditorProps<SourceAccountType, TargetAccountType>) => {
  return (
    <>
      <div className={classes.transferBlock}>
        <CashActionAccountSelector<SourceAccountType>
          isSource
          accountCurrency={selectedCurrency}
          selectedAccount={selectedSourceAccount}
          accountOptions={sourceAccountOptions}
          availableAmount={sourceAccountAmount}
          handleAmountChange={handleAmountChange}
          handleAccountChange={handleSourceAccountChange}
          setAmountToMaxLabel={setAmountToMaxLabel}
          showErrorBorder={noTargetAccountsAvailable}
        />
        <ArrowRightIcon className={classes.arrowIcon} />
        <CashActionAccountSelector<TargetAccountType>
          accountCurrency={selectedCurrency}
          selectedAccount={selectedTargetAccount}
          accountOptions={targetAccountOptions}
          availableAmount={targetAccountAmount}
          handleAccountChange={handleTargetAccountChange}
          disabled={noTargetAccountsAvailable}
        />
      </div>
      {noTargetAccountsAvailable && (
        <CashActionNoSourceOrTargetAccountErrorFeedback
          cashActionType={cashActionType}
          selectedCurrency={selectedCurrency}
          sourceAccountSelected={isDefined(selectedSourceAccount)}
        />
      )}
      {!noTargetAccountsAvailable && (
        <NbNumericFormat
          className={classes.numericInput}
          fullWidth
          value={amount}
          autoComplete="off"
          decimalScale={Number.isInteger(sourceAccountAmount) ? 0 : 2}
          size="small"
          onValueChange={e => handleAmountChange(e.value)}
          InputProps={{
            endAdornment: (
              <div className={classes.currencyAdornment}>
                <p>{selectedCurrency}</p>
              </div>
            ),
          }}
          customInput={NbTextField}
          error={isDefined(numberInputError)}
          errorMessage={numberInputError}
        />
      )}
    </>
  );
};
